import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { ReviewerRequestInput, useRequestReportReviewMutation } from "@api/graphql/types-and-hooks";
import { invalidateReportReviewers, settleReviewMutation } from "@components/QaReview/hooks/reviewMutations";
import { INCLUDE_INTERNAL_REVIEWERS } from "@components/ReportReview/reviewers";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useReportOptional } from "@providers/ReportProvider";

export const useReviewRequest = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const reportId = useReportOptional()?.report?.id;
  const successTextRef = useRef<string | undefined>(undefined);

  const mutation = useRequestReportReviewMutation({
    onError: () => notifyError("Error requesting review. Please try again."),
    onSuccess: async (data) => {
      const override = successTextRef.current;
      const toasts = { failure: "Failed to request review", success: override ? undefined : "Review requested" };
      if (!settleReviewMutation(data?.requestReportReview, toasts)) return;
      if (override) notifySuccess(override);
      await invalidateReportReviewers(queryClient);
      onSuccess?.();
    },
  });

  return {
    isPending: mutation.isPending,
    /** `success` replaces the server's "Review requested" toast, for changes that aren't a new request. */
    request: (reviewers: ReviewerRequestInput[], success?: string) => {
      successTextRef.current = success;
      if (reportId) mutation.mutate({ reportId, includeInternal: INCLUDE_INTERNAL_REVIEWERS, reviewers });
    },
  };
};
